import React, { ReactElement, useEffect } from 'react'
import { useAppDispatch, useAppSelector } from 'store/hooks'
import { BarryPage, removeChatEndsAt, resetMessageState, switchPage } from 'store/barry/barry'
import useBarryViewState from 'components/Barry/hooks/useBarryController '
import { useLocation } from '@reach/router'
import DashboardBarryDialog from './DashboardBarry/DashboardBarryDialog'
import StaticBarryDialog from './StaticPagesBarry/StaticBarryDialog'
import { useEndConversationMutation } from 'store/api/barry'
import { Flex } from 'theme-ui'
import useQueryString from 'utils/useQueryString'

enum BarryQueryStringActions {
  WAKEUP = 'wakeup',
  START = 'start',
}

export default function BarryDialog(): ReactElement {
  const dispatch = useAppDispatch()
  const location = useLocation()
  const pathname = location.pathname
  const isDashboard = location.pathname.includes('dashboard')
  const page = useAppSelector(x => x.barry.barryPage)
  const isManuallyLoggedOut = useAppSelector(s => s.session.manuallyLoggedOut)
  const { chatEndsAt, messages, conversationId } = useAppSelector(x => x.barry.message)
  const { qs } = useQueryString()
  const [endConversationRequest] = useEndConversationMutation()
  const { setBarryDialogOpen, isBarryDialogOpen } = useBarryViewState()

  useEffect(() => {
    const shouldStartBarry = qs.barry === BarryQueryStringActions.START
    const shouldWakeupBarry = qs.barry === BarryQueryStringActions.WAKEUP

    if (shouldStartBarry || shouldWakeupBarry) {
      if (shouldStartBarry) {
        if (messages.length > 1) {
          dispatch(resetMessageState())
          endConversationRequest({ conversationId })
          dispatch(switchPage(BarryPage.DESCRIBE_ISSUE_PAGE))
        } else if (!messages.length) {
          dispatch(switchPage(BarryPage.DESCRIBE_ISSUE_PAGE))
        }
      }
      setBarryDialogOpen(true)
    }
  }, [
    pathname,
    conversationId,
    dispatch,
    endConversationRequest,
    messages.length,
    page,
    qs.barry,
    isBarryDialogOpen,
    setBarryDialogOpen,
  ])

  useEffect(() => {
    const exitConvoIfExpired = () => {
      const currentTime = new Date().getTime()
      if (chatEndsAt && currentTime > chatEndsAt) {
        if (messages.length !== 0) {
          dispatch(resetMessageState())
          endConversationRequest({ conversationId })
        }
        dispatch(removeChatEndsAt())
        dispatch(switchPage(BarryPage.SPLASH_PAGE))
      }
    }

    const checkIfTabIsVisible = () => {
      if (document.visibilityState === 'visible' && page !== BarryPage.SPLASH_PAGE) {
        exitConvoIfExpired()
      }
    }

    document.addEventListener('visibilitychange', checkIfTabIsVisible)

    return () => {
      document.removeEventListener('visibilitychange', checkIfTabIsVisible)
    }
  }, [chatEndsAt, dispatch, endConversationRequest, conversationId, page, messages])

  useEffect(() => {
    if (isManuallyLoggedOut) {
      dispatch(resetMessageState())
      dispatch(switchPage(BarryPage.SPLASH_PAGE))
    }
  }, [isManuallyLoggedOut, dispatch])

  return <Flex>{isDashboard ? <DashboardBarryDialog /> : <StaticBarryDialog />}</Flex>
}
